import { type ComponentProps, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { GridBoxListItem, LargeIcon } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type { Option } from 'modules/core/components/Popover/Content'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  PERMISSION_GROUP_KIND_ADMIN,
  PERMISSION_GROUP_KIND_CUSTOM,
  PERMISSION_GROUP_KIND_DEFAULT,
  PERMISSION_GROUP_KIND_ONBOARDING,
} from 'modules/permissions/lib/constants'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'

import ApplyTemplateModal from './ApplyTemplateModal'
import DeleteModal from './DeleteModal'
import DuplicateModal from './DuplicateModal'
import EditModal from './EditModal'

type GridBoxListItemProps = ComponentProps<typeof GridBoxListItem>

interface Props {
  permissionGroup: PermissionGroup
  memberCount: number
  memberIds: string[]
  canDuplicate: boolean
  canApplyRoleTemplates: boolean
  canEditGroup: boolean
}

const ILLUSTRATION_MAP: Record<string, LargeIcon> = {
  [PERMISSION_GROUP_KIND_ONBOARDING]: 'rocket',
  [PERMISSION_GROUP_KIND_ADMIN]: 'crown',
  [PERMISSION_GROUP_KIND_DEFAULT]: 'user',
  [PERMISSION_GROUP_KIND_CUSTOM]: 'user',
}

function getLabelText(kind: string): string | null | undefined {
  if (kind === PERMISSION_GROUP_KIND_CUSTOM) {
    return i18n.t('common.custom')
  }
  return null
}

const Permissions = ({
  permissionGroup,
  memberCount,
  memberIds,
  canDuplicate,
  canApplyRoleTemplates,
  canEditGroup,
}: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)

  const [applyTemplateModalOpen, setApplyTemplateModalOpen] = useState(false)

  const itemOptions = (): Option[] => {
    const options: GridBoxListItemProps['options'] = []

    if (permissionGroup.attributes.kind === PERMISSION_GROUP_KIND_ONBOARDING) {
      return options
    }

    if (
      permissionGroup.attributes.kind === PERMISSION_GROUP_KIND_ADMIN ||
      permissionGroup.attributes.legacy_admin ||
      !canEditGroup
    ) {
      return options
    }

    options.push({
      title: i18n.t('settings.permissions.users.access_groups'),
      onClick: () =>
        navigate(
          navigation.settings.permissions.id.visibility.fullPath({ id: permissionGroup.id })
        ),
    })

    if (canDuplicate) {
      options.push({
        title: i18n.t('settings.permissions.duplicate_group'),
        onClick: () => setDuplicateModalOpen(!duplicateModalOpen),
      })
    }

    if (!permissionGroup.isDefaultGroup) {
      options.push({
        title: i18n.t('settings.permissions.rename_group'),
        onClick: () => setEditModalOpen(!editModalOpen),
      })
    }

    if (canApplyRoleTemplates) {
      options.push({
        title: i18n.t('settings.permissions.apply_role_template.label'),
        onClick: () => setApplyTemplateModalOpen(true),
      })
    }

    if (!permissionGroup.isDefaultGroup) {
      options.push({
        type: 'wrong',
        title: i18n.t('settings.permissions.delete_group'),
        onClick: () => setDeleteModalOpen(!deleteModalOpen),
      })
    }
    return options
  }

  const onClickAction = () => {
    return navigate(navigation.settings.permissions.id.fullPath({ id: permissionGroup.id }))
  }

  const onClose = () => {
    setEditModalOpen(false)
    setDeleteModalOpen(false)
    setDuplicateModalOpen(false)
    setApplyTemplateModalOpen(false)
  }

  return (
    <GridBoxListItem
      title={permissionGroup.title}
      key={permissionGroup.attributes.id}
      actionText={i18n.t('settings.permissions.list.item.action')}
      illustrationName={ILLUSTRATION_MAP[permissionGroup.attributes.kind] || 'user'}
      labelText={getLabelText(permissionGroup.attributes.kind)}
      onClickAction={onClickAction}
      options={itemOptions()}
      icon="ellipsis"
    >
      {permissionGroup.attributes.kind !== PERMISSION_GROUP_KIND_ONBOARDING &&
        i18n.tp('settings.permissions.list.item.users', {
          count: memberCount,
        })}
      {editModalOpen && <EditModal onClose={onClose} permissionGroup={permissionGroup} />}
      {deleteModalOpen && (
        <DeleteModal
          onClose={onClose}
          permissionGroup={permissionGroup}
          memberCount={memberCount}
          memberIds={memberIds}
        />
      )}
      {duplicateModalOpen && <DuplicateModal onClose={onClose} permissionGroup={permissionGroup} />}
      {applyTemplateModalOpen && (
        <ApplyTemplateModal
          permissionGroupId={permissionGroup.id}
          permissionGroupName={permissionGroup.attributes.name}
          onClose={onClose}
        />
      )}
    </GridBoxListItem>
  )
}

export default observer(Permissions)
